import type { GallerySort } from '@lite-workspace/dto-fotoclass-gallery';

// ONE sort vocabulary for both sides — the client renders these items and sends the chosen one as
// `sort`, the gallery-service validates against the same list. A literal spelled by hand here would
// be a sort that silently falls back to the default, so nothing is re-declared.
export type { GallerySort };

export type GalleryStatus = 'published' | 'hidden';

// Mirrors the server's GalleryCountsDto — the same four numbers travel on a list row
// and on a trash row. `uniqueSessions` is an honest 0 from the server until the
// statistics story lands; the cabinet shows what the server sends, never a guess.
export interface GalleryCounts {
  views: number;
  uniqueSessions: number;
  sizeBytes: number;
  urlSlug: string | null;
}

export interface GalleryListItem extends GalleryCounts {
  id: string;
  title: string;
  status: GalleryStatus;
  shootDate: string | null;
  createdAt: string;
  coverThumbnailUrl: string | null;
  photoCount: number;
  categoryIds: string[];
}

export interface GalleryCategoryFilterOption {
  id: string;
  name: string;
  galleryCount: number;
}

export interface GalleryTrashItem extends GalleryCounts {
  id: string;
  title: string;
  deletedAt: string;
  // ISO retention end; past it the restore answers 409 GALLERY_EXPIRED.
  expiresAt: string | null;
  coverThumbnailUrl: string | null;
  photoCount: number;
  // The same three the active row carries — the trash sorts, filters and dates on them.
  shootDate: string | null;
  createdAt: string;
  categoryIds: string[];
}

export interface GalleryPage<T> {
  items: T[];
  total: number;
  hasMore: boolean;
  nextCursor: string | null;
}

export interface CreatedGallery {
  galleryId: string;
  title: string;
  urlSlug: string;
  publicUrl: string;
}

export type CreateGalleryResult =
  // presetWarnings: preset groups that failed to apply (gallery itself created).
  // guestPasscodeSet: the preset minted a random gate code the owner never saw.
  | { ok: true; gallery: CreatedGallery; presetWarnings?: string[]; guestPasscodeSet?: boolean }
  | { ok: false; reason: 'invalid' | 'error' };

export const DEFAULT_GALLERY_SORT: GallerySort = 'created-desc';

/** The gallery's own date is the shoot date, the creation date only when the shoot date is unset. */
export const galleryDate = (gallery: Pick<GalleryListItem, 'shootDate' | 'createdAt'>): string =>
  gallery.shootDate ?? gallery.createdAt ?? '';

/**
 * ponytail: orders the loaded page, not the whole account — with a query or a category on the server
 * answers in full, but the unfiltered list is paginated, so «по имени» sorts what is already loaded.
 * Server-side ordering is the upgrade path once the list API takes a sort key.
 */
export function sortGalleries(items: readonly GalleryListItem[], sort: GallerySort): GalleryListItem[] {
  const out = items.slice();
  if (sort === 'name') out.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
  else if (sort.startsWith('shoot')) out.sort((a, b) => galleryDate(b).localeCompare(galleryDate(a)));
  if (sort.endsWith('-asc')) out.reverse();
  return out;
}
